'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Search } from 'lucide-react';
import { Icon } from '@/components/ui/Icon';
import { searchArticles } from '@/lib/help/search';
import { helpPath } from '@/lib/help/href';
import type { HelpArticle } from '@/lib/help/types';

/**
 * The search box in the help centre's top banner.
 *
 * Ranks with the same `searchArticles` as the intro page's search, so the two
 * boxes can never disagree about what a query finds. The banner is on every
 * help page, so the layout hands this only what ranking reads — never the
 * article bodies.
 */
export type TopbarSearchArticle = Pick<
  HelpArticle,
  'category' | 'slug' | 'title' | 'summary' | 'keywords'
>;

export default function HelpTopbarSearch({ articles }: { articles: TopbarSearchArticle[] }) {
  const router = useRouter();
  const pathname = usePathname();
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const results = useMemo(
    // searchArticles reads title, summary and keywords only.
    () => searchArticles(query, articles as HelpArticle[], 6),
    [query, articles],
  );
  const typed = query.trim().length > 1;

  // Leaving the page closes the dropdown and clears the box.
  const [shownOn, setShownOn] = useState(pathname);
  if (shownOn !== pathname) {
    setShownOn(pathname);
    setOpen(false);
    setQuery('');
  }

  // "/" focuses the box, as its hint promises — unless you're already typing somewhere.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      inputRef.current?.focus();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    } else if (e.key === 'ArrowDown' && results.length) {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp' && results.length) {
      e.preventDefault();
      setActive((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === 'Enter' && open && results[active]) {
      e.preventDefault();
      const a = results[active];
      router.push(helpPath(`/${a.category}/${a.slug}`));
    }
  }

  return (
    <div className="help-topbar-search" ref={boxRef}>
      <Icon icon={Search} size={16} className="help-topbar-search-icon" />
      <input
        ref={inputRef}
        type="search"
        className="help-topbar-search-input"
        placeholder="Search the help centre"
        aria-label="Search help centre"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {!query && <span className="help-topbar-search-kbd">/</span>}
      {open && typed && (
        <div className="help-topbar-search-panel">
          {results.length > 0 ? (
            <ul className="help-search-results">
              {results.map((a, i) => (
                <li key={`${a.category}/${a.slug}`}>
                  <Link
                    href={helpPath(`/${a.category}/${a.slug}`)}
                    className={`help-search-result${i === active ? ' is-active' : ''}`}
                    onMouseEnter={() => setActive(i)}
                  >
                    <div className="help-search-result-title">{a.title}</div>
                    <div className="help-search-result-summary">{a.summary}</div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="help-search-empty">
              No matches. Try a different phrase, or ask Mucka inside the app.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
